/**
 * VersionService for managing versioned data operations
 */
import axios from 'axios';
import { API_BASE_URL } from '../config';
import cacheService from './cacheService';
import { createErrorLogger, ErrorType } from '../utils/logger';

const logger = createErrorLogger('VersionService');

class VersionService {
  /**
   * Save versioned data
   * @param {string} userId - User ID
   * @param {string} component - Component name
   * @param {string} filename - File name
   * @param {any} data - Data to save
   * @param {string} author - Author name
   * @param {object} options - Additional options
   * @returns {Promise<object>} Save result
   */
  async saveVersionedData(userId, component, filename, data, author = 'User', options = {}) {
    logger.info('saveVersionedData', `Saving versioned data: ${component}/${filename} for user ${userId}`);

    // Input validation with detailed logging
    if (!userId) {
      throw new Error('saveVersionedData requires a valid userId');
    }

    // Ensure userId is a string
    const userIdStr = typeof userId === 'string' ? userId : (() => { throw new Error('userId must be a string'); })();
    logger.info('saveVersionedData', `Using userId: ${userIdStr}`);

    // Validate data
    let jsonData;
    if (typeof data === 'string') {
      jsonData = data;
    } else if (data === null || data === undefined) {
      throw new Error('saveVersionedData requires non-null data');
    } else {
      try {
        jsonData = JSON.stringify(data);
      } catch (error) {
        logger.error('saveVersionedData', 'Error stringifying data', error, ErrorType.VALIDATION);
        throw new Error(`saveVersionedData could not serialize data: ${error.message}`);
      }
    }

    try {
      // Create FormData for the API call
      const formData = new FormData();
      formData.append('user_id', userIdStr);
      formData.append('component', component);
      formData.append('filename', filename);
      formData.append('data', jsonData);
      formData.append('author', author); // Include author information

      // Add version control parameters if provided in options
      if (options.preventVersion) {
        logger.info('saveVersionedData', 'Explicitly preventing version creation');
        formData.append('prevent_version', 'true');
      }

      if (options.forceVersion) {
        logger.info('saveVersionedData', 'Explicitly forcing version creation');
        formData.append('force_version', 'true');
      }

      // Add operation and description if provided
      if (options.operation) {
        formData.append('operation', options.operation);
      }

      if (options.description) {
        formData.append('description', options.description);
      }

      // Log the form data for debugging
      logger.info('saveVersionedData', 'Form data prepared', {
        user_id: userIdStr,
        component,
        filename,
        data_preview: jsonData.substring(0, 50) + (jsonData.length > 50 ? '...' : ''),
        author
      });

      const response = await axios.post(`${API_BASE_URL}/system/user/save_versioned_data`, formData);
      logger.info('saveVersionedData', 'Save versioned data response', { response: response.data });

      // Log whether a new version was created
      if (response.data.version_created !== undefined) {
        logger.info('saveVersionedData', `New version created: ${response.data.version_created}`);
      }

      // Update cache
      cacheService.clear(`${userIdStr}_${component}_${filename}`);

      return {
        status: 'success',
        ...response.data?.result
      };
    } catch (error) {
      logger.error('saveVersionedData', 'Error saving versioned data', error, ErrorType.DATA_SAVE, { userId: userIdStr, component, filename });
      throw error;
    }
  }

  /**
   * Load versioned data
   * @param {string} userId - User ID
   * @param {string} component - Component name
   * @param {string} filename - File name
   * @param {string|null} version - Version to load (null for latest)
   * @returns {Promise<any>} Loaded data
   */
  async loadVersionedData(userId, component, filename, version = null) {
    logger.info('loadVersionedData', `Loading versioned data: ${component}/${filename} for user ${userId}`);

    // Check cache first (only for latest version)
    const cacheKey = `${userId}_${component}_${filename}`;
    if (!version) {
      const cached = cacheService.get(cacheKey);
      if (cached) {
        logger.info('loadVersionedData', `Returning cached data for ${cacheKey}`);
        return cached;
      }
    }

    try {
      const params = new URLSearchParams({
        user_id: userId,
        component: component,
        filename: filename
      });

      if (version) {
        params.append('version', version);
      }

      const response = await axios.get(`${API_BASE_URL}/system/user/load_versioned_data?${params}`);
      logger.info('loadVersionedData', 'Load versioned data response', { response: response.data });

      // Cache the result if it's the latest version
      if (!version && response.data) {
        cacheService.set(cacheKey, response.data);
      }

      return response.data || {};
    } catch (error) {
      logger.error('loadVersionedData', 'Error loading versioned data', error, ErrorType.DATA_LOAD, { userId, component, filename });
      if (error.response?.status === 404) {
        logger.info('loadVersionedData', `File not found: ${component}/${filename}`);
        return null;
      }
      throw error;
    }
  }

  /**
   * List all versions of a file
   * @param {string} userId - User ID
   * @param {string} component - Component name
   * @param {string} filename - File name
   * @returns {Promise<array>} List of versions
   */
  async listVersions(userId, component, filename) {
    logger.info('listVersions', `Listing versions for ${component}/${filename}, user ${userId}`);

    try {
      const response = await axios.get(`${API_BASE_URL}/system/user/list_versions`, {
        params: { user_id: userId, component, filename }
      });

      const versions = response.data.result || response.data.versions || [];
      logger.info('listVersions', `Found ${versions.length} versions`);
      return versions;
    } catch (error) {
      logger.error('listVersions', 'Error listing versions', error, ErrorType.VERSION, { userId, component, filename });
      return [];
    }
  }

  /**
   * Get all versions for a user and type
   * @param {string} type - Data type
   * @param {string} userId - User ID
   * @returns {Promise<array>} List of versions
   */
  async getUserVersions(type, userId) {
    logger.info('getUserVersions', `Getting user versions for type: ${type}, userId: ${userId}`);

    // Map type to component/filename
    let component, filename;
    switch (type) {
      case 'cv':
        component = 'cv_analyzer';
        filename = 'result.json';
        break;
      case 'job':
        component = 'job_analyzer';
        filename = 'result.json';
        break;
      case 'company':
        component = 'company_research';
        filename = 'result.json';
        break;
      default:
        logger.warn('getUserVersions', `Unknown type: ${type}`);
        return [];
    }

    try {
      const versions = await this.listVersions(userId, component, filename);
      logger.info('getUserVersions', `Retrieved ${versions.length} versions for ${type}`);

      // Sort versions by timestamp (newest first)
      return versions.sort((a, b) => {
        const dateA = new Date(a.timestamp || a.created_at || a.modified);
        const dateB = new Date(b.timestamp || b.created_at || b.modified);
        return dateB - dateA;
      });
    } catch (error) {
      logger.error('getUserVersions', 'Error getting user versions', error, ErrorType.VERSION, { type, userId });
      return [];
    }
  }

  /**
   * Save version log entry
   * @param {number} version - Version number
   * @param {string} description - Version description
   * @param {string|null} userId - User ID
   * @param {string|null} component - Component name
   * @returns {Promise<object>} Save result
   */
  async saveVersionLogEntry(version, description, userId = null, component = null) {
    logger.info('saveVersionLogEntry', 'Saving version log entry', { version, description, userId, component });

    try {
      const logData = {
        version,
        description,
        timestamp: new Date().toISOString()
      };

      // If userId and component are provided, save to user-specific log
      if (userId && component) {
        const existingLogs = await this.loadVersionedData(userId, component, 'version_log.json') || [];
        existingLogs.push(logData);

        return await this.saveVersionedData(
          userId,
          component,
          'version_log.json',
          existingLogs,
          'System',
          { preventVersion: true }
        );
      } else {
        // Save to global version log
        const existingLogs = await this.loadVersionedData('admin', 'system', 'global_version_log.json') || [];
        existingLogs.push(logData);

        return await this.saveVersionedData(
          'admin',
          'system',
          'global_version_log.json',
          existingLogs,
          'System',
          { preventVersion: true }
        );
      }
    } catch (error) {
      logger.error('saveVersionLogEntry', 'Error saving version log entry', error, ErrorType.VERSION);
      return { status: 'error', message: error.message };
    }
  }

  /**
   * Get version logs
   * @param {string|null} userId - User ID
   * @param {string|null} component - Component name
   * @returns {Promise<array>} Version logs
   */
  async getVersionLogs(userId = null, component = null) {
    logger.info('getVersionLogs', 'Getting version logs', { userId, component });

    try {
      if (userId && component) {
        return await this.loadVersionedData(userId, component, 'version_log.json') || [];
      } else {
        return await this.loadVersionedData('admin', 'system', 'global_version_log.json') || [];
      }
    } catch (error) {
      logger.error('getVersionLogs', 'Error getting version logs', error, ErrorType.VERSION);
      return [];
    }
  }

  /**
   * Restore version by creating a new version with old data (no data loss)
   * @param {string} userId - User ID
   * @param {string} component - Component name
   * @param {string} filename - Target filename (usually 'result.json')
   * @param {string} versionFilename - Source version filename to restore from
   * @returns {Promise<object>} Restore result
   */
  async restoreVersionAsNew(userId, component, filename, versionFilename) {
    logger.info('restoreVersionAsNew', `Restoring version as new: ${component}/${versionFilename} for user ${userId}`);

    // Input validation
    if (!userId || !component || !filename || !versionFilename) {
      throw new Error('restoreVersionAsNew requires userId, component, filename, and versionFilename');
    }

    try {
      // Create FormData for the API call
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('component', component);
      formData.append('filename', filename);
      formData.append('version_filename', versionFilename);

      logger.info('restoreVersionAsNew', 'Form data prepared', {
        user_id: userId,
        component,
        filename,
        version_filename: versionFilename
      });

      const response = await axios.post(`${API_BASE_URL}/system/user/restore_version_as_new`, formData);
      logger.info('restoreVersionAsNew', 'Restore version as new response', { response: response.data });

      // Clear cache for the component to ensure fresh data load
      cacheService.clear(`${userId}_${component}_${filename}`);

      return response.data;
    } catch (error) {
      logger.error('restoreVersionAsNew', 'Error restoring version as new', error, ErrorType.VERSION, {
        userId,
        component,
        filename,
        versionFilename
      });

      if (error.response?.status === 400) {
        throw new Error(`Failed to restore version: ${error.response.data.detail || error.message}`);
      }

      throw new Error(`Failed to restore version: ${error.message}`);
    }
  }
}

// Create singleton instance
const versionService = new VersionService();

// Export singleton instance
export default versionService;
